import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export const BRAND_COLOUR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#B8860B', general: '#6B7280' };
export const SLOT_LABEL = { day: 'All day', am: 'Morning', pm: 'Afternoon', hours: 'Hours' };

export const iso = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
export const todayIso = () => iso(new Date());
export const fmtDay = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
export const fmtLong = (s) => new Date(s).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
export const coversDay = (b, dayIso) => b.start_date <= dayIso && b.end_date >= dayIso;

async function api(path, opts = {}) {
  const res = await fetch(`${API_URL}/api${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiError(data) || 'Request failed');
  return data;
}

export function useSchedule({ from, to, installerId, projectId } = {}) {
  const [data, setData] = useState({ blocks: [], projects: [], crew: [], marketing_calls: [], read_only: false });
  const [weather, setWeather] = useState({});
  const [rainRisks, setRainRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  const reloadWeather = useCallback(async () => {
    if (!from || !to || projectId) return;
    try {
      const q = new URLSearchParams({ from, to });
      if (installerId) q.set('installer_id', installerId);
      const w = await api(`/schedule/weather?${q}`);
      setWeather(w.days || {});
      setRainRisks(w.rain_risks || []);
    } catch { /* weather is best-effort */ }
  }, [from, to, installerId, projectId]);

  const reload = useCallback(async () => {
    if (!from || !to) return;
    try {
      const q = new URLSearchParams({ from, to });
      if (installerId) q.set('installer_id', installerId);
      if (projectId) q.set('project_id', projectId);
      setData(await api(`/schedule?${q}`));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, installerId, projectId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadWeather(); }, [reloadWeather]);

  const wrap = (fn, ok) => async (...a) => {
    try { const r = await fn(...a); if (ok) toast.success(ok); await reload(); reloadWeather(); return r; }
    catch (e) { toast.error(e.message); throw e; }
  };

  return {
    ...data, weather, rainRisks, loading, reload,
    previewDaySheet: (body) => api('/schedule/day-sheet/preview', { method: 'POST', body: JSON.stringify(body) }),
    sendDaySheet: (body) => api('/schedule/day-sheet/send', { method: 'POST', body: JSON.stringify(body) }),
    createBlock: wrap((body) => api('/schedule/blocks', { method: 'POST', body: JSON.stringify(body) }), 'Added to calendar'),
    updateBlock: wrap((id, body) => api(`/schedule/blocks/${id}`, { method: 'PUT', body: JSON.stringify(body) }), 'Updated'),
    deleteBlock: wrap((id) => api(`/schedule/blocks/${id}`, { method: 'DELETE' }), 'Removed'),
    shiftBlock: wrap((id, days) => api(`/schedule/blocks/${id}/shift`, { method: 'POST', body: JSON.stringify({ days }) })),
    duplicateBlock: wrap((id, days) => api(`/schedule/blocks/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ days }) }), 'Duplicated'),
    addCrew: wrap((body) => api('/schedule/crew', { method: 'POST', body: JSON.stringify(body) }), 'Crew member added'),
    updateCrew: wrap((id, body) => api(`/schedule/crew/${id}`, { method: 'PUT', body: JSON.stringify(body) })),
    deleteCrew: wrap((id) => api(`/schedule/crew/${id}`, { method: 'DELETE' }), 'Crew member removed'),
  };
}
